/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent } from "./components/ui/card";
import { toast } from "sonner";
import { getFormationById } from "./data/formations";
import { NDA_NUMBER } from "./components/NdaMention";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { type_client: "particulier", prenom: "", nom: "", email: "", telephone: "", adresse: "", entreprise: "", siret: "" };

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm font-semibold text-[#003087]">{label}</Label>
    {children}
  </div>
);

export default function CheckoutPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [formation, setFormation] = useState(undefined);
  const local = useMemo(() => getFormationById(code), [code]);
  useEffect(() => {
    axios.get(`${API}/paiement/formations/${code}`).then((r) => setFormation({ ...r.data, slug: local?.slug })).catch(() => setFormation(null));
  }, [code, local]);
  const [form, setForm] = useState(EMPTY);
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [renonciation, setRenonciation] = useState(false);
  const [consentRgpd, setConsentRgpd] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState("");
  useEffect(() => {
    axios.get(`${API}/paiement/config`).then((r) => setPaypalClientId(r.data.client_id)).catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (formation === undefined) return <div className="pt-32 pb-20 container-custom text-center text-gray-500">Chargement…</div>;
  if (!formation) {
    return <div className="pt-32 pb-20 container-custom text-center" data-testid="checkout-not-found"><p className="text-gray-600">Formation introuvable.</p><Link to="/catalogue" className="text-[#F07B1D] font-bold">Retour au catalogue</Link></div>;
  }

  const isPro = form.type_client === "entreprise";
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const ready = form.prenom.trim() && form.nom.trim() && emailOk && acceptCgv && consentRgpd && (!isPro || form.entreprise.trim()) && (isPro || renonciation);

  const createOrder = async () => {
    try {
      const res = await axios.post(`${API}/paiement/orders`, { formation_code: formation.code, ...form, accept_cgv: acceptCgv, renonciation_retractation: renonciation, consent_rgpd: consentRgpd });
      return res.data.order_id;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'initialiser le paiement");
      throw err;
    }
  };

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/paiement/orders/${data.orderID}/capture`);
      navigate(`/paiement/confirme?commande=${res.data.commande_id}&facture=${res.data.numero_facture || ""}&formation=${formation.code}&amount=${formation.price}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Le paiement n'a pas pu être confirmé — contactez-nous");
      setProcessing(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F8FAFC]" data-testid="checkout-page">
      <div className="container-custom max-w-5xl">
        <Link to={formation.slug ? `/formations/${formation.slug}` : formation.code.startsWith("OTM") ? "/otm" : "/catalogue"} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#F07B1D] mb-4" data-testid="checkout-back"><ArrowLeft className="h-4 w-4" /> Retour à la formation</Link>
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <div>
              <p className="text-[#F07B1D] font-semibold text-xs uppercase tracking-wider mb-1">Inscription en ligne</p>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] leading-tight">Vos informations</h1>
            </div>
            <Card><CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-2" data-testid="checkout-type-client">
                {[["particulier", "Particulier"], ["entreprise", "Entreprise"]].map(([v, lab]) => (
                  <button key={v} type="button" onClick={() => set("type_client", v)} className={`py-2.5 rounded-lg border-2 font-semibold text-sm transition-colors ${form.type_client === v ? "border-[#003087] bg-[#003087] text-white" : "border-gray-200 text-gray-600 hover:border-[#003087]"}`} data-testid={`checkout-type-${v}`}>{lab}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Prénom *" id="prenom"><Input id="prenom" value={form.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="checkout-prenom" /></Field>
                <Field label="Nom *" id="nom"><Input id="nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} data-testid="checkout-nom" /></Field>
                <Field label="E-mail *" id="email"><Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="checkout-email" /></Field>
                <Field label="Téléphone" id="tel"><Input id="tel" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="checkout-telephone" /></Field>
                {isPro && (
                  <>
                    <Field label="Entreprise *" id="entreprise"><Input id="entreprise" value={form.entreprise} onChange={(e) => set("entreprise", e.target.value)} data-testid="checkout-entreprise" /></Field>
                    <Field label="SIRET" id="siret"><Input id="siret" value={form.siret} onChange={(e) => set("siret", e.target.value)} data-testid="checkout-siret" /></Field>
                  </>
                )}
                <div className="sm:col-span-2"><Field label="Adresse de facturation" id="adresse"><Input id="adresse" value={form.adresse} onChange={(e) => set("adresse", e.target.value)} data-testid="checkout-adresse" /></Field></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="p-6 space-y-3 text-sm text-gray-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F07B1D]" checked={acceptCgv} onChange={(e) => setAcceptCgv(e.target.checked)} data-testid="checkout-cgv" />
                <span>J'ai lu et j'accepte les <Link to="/cgv" target="_blank" className="text-[#F07B1D] underline font-medium">Conditions Générales de Vente</Link> et le programme de la formation ; {isPro ? "je commande au nom de mon entreprise (une convention de formation me sera adressée)." : "ce paiement vaut acceptation du contrat de formation professionnelle (art. L6353-3 C. trav.), qui me sera adressé par e-mail."} *</span>
              </label>
              {!isPro && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F07B1D]" checked={renonciation} onChange={(e) => setRenonciation(e.target.checked)} data-testid="checkout-renonciation" />
                  <span>Je demande un accès immédiat aux contenus et <strong>renonce expressément à mon délai de rétractation de 10 jours</strong> (art. L6353-5 C. trav. / L221-28 C. conso.) ; à défaut, je peux contacter MGB School pour une inscription différée sans paiement immédiat. *</span>
                </label>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F07B1D]" checked={consentRgpd} onChange={(e) => setConsentRgpd(e.target.checked)} data-testid="checkout-rgpd" />
                <span>J'accepte que mes données soient traitées par MGB School pour la gestion de mon inscription, la facturation et le suivi pédagogique (<Link to="/confidentialite" target="_blank" className="text-[#F07B1D] underline font-medium">politique de confidentialité</Link>). *</span>
              </label>
            </CardContent></Card>
          </div>

          <div className="lg:col-span-2">
            <Card className="sticky top-28 border-t-4 border-t-[#F07B1D]"><CardContent className="p-6">
              <p className="text-xs uppercase tracking-wider font-bold text-[#F07B1D] mb-1">{formation.domain}</p>
              <h2 className="font-heading text-lg font-bold text-[#003087] leading-tight mb-3" data-testid="checkout-formation-title">{formation.title}</h2>
              <p className="text-xs text-gray-500 mb-4">{formation.code} · {formation.hours} h · {formation.modality}</p>
              <div className="flex items-baseline justify-between border-t border-b border-gray-100 py-3 mb-4">
                <span className="text-sm text-gray-600">Total à régler</span>
                <span className="font-heading text-3xl font-extrabold text-[#003087]" data-testid="checkout-amount">{formation.price} €</span>
              </div>
              <p className="text-[11px] text-gray-500 mb-4">Net de taxe — exonération de TVA art. 261-4-4° CGI · Organisme déclaré NDA {NDA_NUMBER} · Facture acquittée envoyée par e-mail.</p>

              {!ready && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3" data-testid="checkout-hint">Complétez vos informations et cochez les cases obligatoires pour afficher le paiement.</p>}
              {processing && <p className="text-sm text-[#003087] font-semibold mb-3" data-testid="checkout-processing">Confirmation du paiement en cours…</p>}
              <div className={ready && !processing ? "" : "opacity-40 pointer-events-none"} data-testid="paypal-buttons-wrapper">
                {paypalClientId ? (
                  <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "EUR", intent: "capture", locale: "fr_FR", components: "buttons", "enable-funding": "card", "disable-funding": "paylater,sepa" }}>
                    <PayPalButtons style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 48 }} forceReRender={[formation.code, form, acceptCgv, renonciation, consentRgpd]} disabled={!ready} createOrder={createOrder} onApprove={onApprove} onError={() => toast.error("Erreur PayPal — veuillez réessayer")} />
                  </PayPalScriptProvider>
                ) : <p className="text-sm text-red-600">Paiement en ligne indisponible.</p>}
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500"><Lock className="h-3.5 w-3.5 text-[#F07B1D]" /> Paiement sécurisé PayPal · Carte bancaire acceptée sans compte PayPal</div>
              <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500"><ShieldCheck className="h-3.5 w-3.5 text-[#F07B1D]" /> Aucune donnée bancaire stockée par MGB School</div>
            </CardContent></Card>
          </div>
        </div>
      </div>
    </div>
  );
}
